import type { ColorType } from 'common/helpers'
import { chunkArray } from 'common/helpers'
import { useEffect, useRef } from 'react'

import type { Nullable, Property, Range2D, ScaleType, UpdateValue } from '../../../../types'
import { convertGlobalGeologicalCoordinatesToLocal, requestRedraw } from '../../../../utils'
import { PointsRender } from '../PointsRender'

const getPoints = (points: Float32Array, origin: Range2D): Float32Array =>
  new Float32Array(
    chunkArray(points, 3).flatMap(point => {
      const coords = convertGlobalGeologicalCoordinatesToLocal(origin, { x: point[0], y: point[1] })
      return [coords.x, coords.y, point[2]]
    }),
  )

export const useShader = (
  containerElement: Nullable<HTMLCanvasElement>,
  programs: Property<Record<string, WebGLProgram>>,
  scale: ScaleType,
  origin: Range2D,
  size: number,
  points: Float32Array,
  color: ColorType,
  setUpdate: UpdateValue<number>,
) => {
  const lineRenderRef = useRef<Nullable<PointsRender>>(null)

  const dispose = () => {
    lineRenderRef.current?.[Symbol.dispose]()
    lineRenderRef.current = null
    requestRedraw(setUpdate)
  }

  useEffect(() => {
    if (lineRenderRef.current || !containerElement) return dispose

    lineRenderRef.current = new PointsRender(
      containerElement,
      programs,
      scale,
      getPoints(points, origin),
      size,
      color,
    )

    requestRedraw(setUpdate)

    return dispose
  }, [containerElement, setUpdate])

  useEffect(() => {
    if (!lineRenderRef.current) return

    lineRenderRef.current.setPoints(getPoints(points, origin))
  }, [points, origin, origin.x, origin.y, origin.width, origin.height, setUpdate])

  useEffect(() => {
    if (!lineRenderRef.current) return

    lineRenderRef.current.setZoomState(scale)

    requestRedraw(setUpdate)
  }, [scale, setUpdate])

  useEffect(() => {
    if (!lineRenderRef.current) return

    lineRenderRef.current.setSize(size)
  }, [size])

  useEffect(() => {
    if (!lineRenderRef.current) return

    lineRenderRef.current.setColor(color)
  }, [color])

  return lineRenderRef.current
}
